import React, { useState, useEffect } from "react";
import { prompts, getTypeFieldIndex, getFormatFieldIndex } from "../components/env";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Select,
  Checkbox
} from "@chakra-ui/react";

const DuplicatePatientModal = ({ isOpen, onClose, onDuplicatePatient, patientData }) => {
  const [formData, setFormData] = useState({});
  const [hour, setHour] = useState('');
  const [minute, setMinute] = useState('');
  const [ampm, setAmpm] = useState('');
  const [nameComponents, setNameComponents] = useState({
    firstName: '',
    lastName: ''
  });

  useEffect(() => {
    if (patientData) {
      // Split the existing name into first and last name
      const nameParts = patientData[prompts[0][0]]?.split(', ') || ['', ''];
      const lastName = nameParts[0] || '';
      const firstName = nameParts[1] || '';

      // Initialize form data with existing patient data if available
      const [h, m] = patientData.time_?.split(':') || ['', ''];
      let formattedHour = '';
      let period = '';

      if (h !== '') {
        const hourInt = parseInt(h, 10);
        period = hourInt >= 12 ? 'PM' : 'AM';
        formattedHour = hourInt % 12 === 0 ? 12 : hourInt % 12;
      }

      setNameComponents({
        firstName: firstName,
        lastName: lastName
      });

      setFormData({
        FirstName: firstName,
        LastName: lastName,
        Email: patientData[prompts[1][0]] || '',
        "Phone Number": patientData[prompts[2][0]] || '', 
        "Class Id": patientData.EHR_ID || patientData["EHR_ID"] || '',
        ...(getFormatFieldIndex() !== null && { "Output Format": patientData[prompts[getFormatFieldIndex()][0]] || '' }),
        Type: patientData[prompts[getTypeFieldIndex()][0]] || '',
        "Scheduled Date": patientData.date_ || '', 
        Forms: patientData.forms_ind || false
      });
      setHour(formattedHour.toString());
      setMinute(m);
      setAmpm(period);
    } else {
      // Clear form data if no patient data is provided
      setFormData({});
      setNameComponents({ firstName: '', lastName: '' });
      setHour('');
      setMinute('');
      setAmpm('');
    }
  }, [patientData, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'FirstName') {
      setNameComponents(prev => ({ ...prev, firstName: value }));
    } else if (name === 'LastName') {
      setNameComponents(prev => ({ ...prev, lastName: value }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  const handleTimeChange = (setter) => (e) => {
    setter(e.target.value);
  };

  const handleSubmit = () => {
    // Combine hour, minute, and ampm into a time string (HH:mm format)
    let hour24 = parseInt(hour, 10);
    if (ampm === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (ampm === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    const scheduledTime = `${String(hour24).padStart(2, '0')}:${minute}`;

    // Format the name as "LastName, FirstName"
    const formattedName = `${formData.LastName}, ${formData.FirstName}`;
    
    const dataToSubmit = { 
      ...formData, 
      "Scheduled Time": scheduledTime,
      name: formattedName
    };
    
    onDuplicatePatient(dataToSubmit);
    onClose();
  };

  const generateHourOptions = () => {
    const options = [];
    for (let i = 1; i <= 12; i++) {
      options.push(String(i).padStart(2, '0'));
    }
    return options;
  };

  const generateMinuteOptions = () => {
    const options = [];
    for (let i = 0; i < 60; i += 15) {
      options.push(String(i).padStart(2, '0'));
    }
    return options;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Duplicate Patient</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <HStack spacing={4} width="100%">
              <FormControl isRequired>
                <FormLabel>First Name</FormLabel>
                <Input
                  name="FirstName"
                  value={formData.FirstName || ''}
                  onChange={handleInputChange}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Last Name</FormLabel>
                <Input
                  name="LastName"
                  value={formData.LastName || ''}
                  onChange={handleInputChange}
                />
              </FormControl>
            </HStack>
            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                name="Email"
                value={formData.Email || ''}
                onChange={handleInputChange}
                type="email"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Phone Number</FormLabel>
              <Input
                name="Phone Number"
                value={formData["Phone Number"] || ''}
                onChange={handleInputChange}
                type="tel"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Class Id</FormLabel>
              <Input
                name="Class Id"
                value={formData["Class Id"] || ''}
                onChange={handleInputChange}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Output Format</FormLabel>
              <Input
                name="Output Format"
                value={formData["Output Format"] || ''}
                onChange={handleInputChange}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Type</FormLabel>
              <Input
                name="Type"
                value={formData.Type || ''}
                onChange={handleInputChange}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Scheduled Date</FormLabel>
              <Input
                name="Scheduled Date"
                value={formData["Scheduled Date"] || ''}
                onChange={handleInputChange}
                type="date"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Scheduled Time</FormLabel>
              <HStack spacing={2}>
                <Select value={hour} onChange={handleTimeChange(setHour)} placeholder="Hour">
                  {generateHourOptions().map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </Select>
                <Select value={minute} onChange={handleTimeChange(setMinute)} placeholder="Minute">
                  {generateMinuteOptions().map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </Select>
                <Select value={ampm} onChange={handleTimeChange(setAmpm)} placeholder="AM/PM">
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </Select>
              </HStack>
            </FormControl>
            <FormControl>
              <Checkbox
                name="Forms"
                isChecked={formData.Forms || false}
                onChange={handleCheckboxChange}
              >
                Forms
              </Checkbox>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={handleSubmit}>
            Duplicate
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DuplicatePatientModal; 